/**
 * 매장별 사용 가능 포인트 조회 훅
 * GET /api/v1/stores/{storeId}/points/available
 *
 * 로그인한 사용자만 조회 가능.
 * storeId가 없거나 미로그인 시 null 반환.
 */
import { useState, useEffect, useRef } from 'react';
import { pointsApi } from '@/lib/api';
import type { StoreAvailablePointsResponse } from '@/lib/api/types';
import { logger } from '@/utils/logger';

export interface AvailablePointsData {
  totalAvailable: number;
  usableAmount: number;
  minUsePoints: number;
  maxUseRatio: number;
}

export interface UseAvailablePointsReturn {
  data: AvailablePointsData | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useAvailablePoints(
  storeId: number | undefined,
  enabled: boolean = true,
  orderAmount?: number
): UseAvailablePointsReturn {
  const [data, setData] = useState<AvailablePointsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    // storeId/enabled 변경 시 이전 데이터 초기화 + stale 응답 무시
    setData(null);
    setError(null);

    if (!storeId || !enabled) {
      setIsLoading(false);
      return;
    }

    const currentFetchId = ++fetchIdRef.current;
    let cancelled = false;

    const doFetch = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response: StoreAvailablePointsResponse = await pointsApi.getStoreAvailablePoints(storeId, orderAmount);

        // stale 응답 무시 (storeId/enabled 변경으로 새 fetch가 시작된 경우)
        if (cancelled || fetchIdRef.current !== currentFetchId) return;

        const d = response.data;
        if (response.success === true && d) {
          setData({
            totalAvailable: d.total_available,
            usableAmount: d.usable_amount,
            minUsePoints: d.min_use_points,
            maxUseRatio: d.max_use_ratio,
          });
        } else {
          setData(null);
          setError('포인트 정보를 불러오지 못했습니다.');
          logger.error('[useAvailablePoints] 유효하지 않은 포인트 응답:', {
            success: response.success === true,
            hasData: response.data != null,
          });
        }
      } catch (err) {
        if (cancelled || fetchIdRef.current !== currentFetchId) return;
        const msg = err instanceof Error ? err.message : '포인트 조회 실패';
        setError(msg);
        logger.error('[useAvailablePoints] 조회 실패:', err);
      } finally {
        if (!cancelled && fetchIdRef.current === currentFetchId) {
          setIsLoading(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
  }, [storeId, enabled, orderAmount]);

  const refresh = async () => {
    if (!storeId || !enabled) return;

    const currentFetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const response: StoreAvailablePointsResponse = await pointsApi.getStoreAvailablePoints(storeId, orderAmount);
      if (fetchIdRef.current !== currentFetchId) return;

      const d = response.data;
      if (response.success === true && d) {
        setData({
          totalAvailable: d.total_available,
          usableAmount: d.usable_amount,
          minUsePoints: d.min_use_points,
          maxUseRatio: d.max_use_ratio,
        });
      } else {
        setData(null);
        setError('포인트 정보를 불러오지 못했습니다.');
        logger.error('[useAvailablePoints] refresh: 유효하지 않은 포인트 응답:', {
          success: response.success === true,
          hasData: response.data != null,
        });
      }
    } catch (err) {
      if (fetchIdRef.current !== currentFetchId) return;
      const msg = err instanceof Error ? err.message : '포인트 조회 실패';
      setError(msg);
    } finally {
      if (fetchIdRef.current === currentFetchId) {
        setIsLoading(false);
      }
    }
  };

  return { data, isLoading, error, refresh };
}
